import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import {COLORS, IconButton, Size} from '..';
import {IconButtonContentStyles, IconButtonStyledPrimary} from '../Button/Iconbutton';
import {NotificationDot} from '../NotificationDot';
import {BREAKPOINTS, ComponentS, ComponentTextStyle} from '../styles';

export type ProfileButtonProps = {
  /**
   * Optional. The source URL of the profile picture.
   */
  portraitSrc?: string;

  /**
   * Optional. The icon of the profile to be displayed in absence of 'portraitSrc' and 'initials'.
   */
  icon?: React.ReactNode;

  /**
   * Optional. The initials of the user. If provided, these will be displayed in circle in the absence of a profile picture.
   */
  initials?: string;

  /**
   * Required. The handler function to be called when the button is clicked.
   */
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;

  /**
   * Optional. The variant of the notification dot. It can be 'positive' or 'critical'.
   */
  notificationVariant?: 'positive' | 'critical';
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> ;

const PortraitOverlay = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${props => COLORS.getColor('black', props.theme)};
  opacity: 0;
  position: absolute;
  top: 0px;
  left: 0px;
`;

export const ProfileButtonContainer = styled.div<{ $hideOnLowWidth?: boolean }>`
  width: 48px;
  height: 48px;
  display: inline;
  user-select: none;
  
  ${IconButtonStyledPrimary} {
    display: ${props => (props.$hideOnLowWidth ? 'none' : 'inline')};

    ${BREAKPOINTS.MEDIUM} {
      display: inline;
    }

    :disabled {
      img {
        filter: grayscale(100%);
      }

      ${IconButtonContentStyles} {
        background-color: ${props => COLORS.generateToken({componentType: 'bg-fill', defaultVariant: 'primary'}, props.theme)};

        svg, svg path {
          fill: ${props => COLORS.generateToken({componentType: 'text', isOnFill: true, defaultVariant: 'primary'}, props.theme)};
        }
      }
    }

    :hover:not(:disabled):not(:active),
    &.hover-state {
      ${PortraitOverlay} {
        opacity: 0.25;
        transition: opacity 100ms ease-in-out;
      }
    }

    :active:not(:disabled),
    &.active-state {
      ${PortraitOverlay} {
        opacity: 0.5;
        transition: opacity 0ms ease-in-out;
      }
    }
  }
`;

const NotificationContainer = styled.div`
  position: absolute;
  left: 30px;
  bottom: 30px;
`;

const ImageWrapper = styled.div`
  width: 36px;
  height: 36px;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const ProfileButton = React.forwardRef<HTMLButtonElement, ProfileButtonProps>(({
                                                                                 portraitSrc,
                                                                                 icon,
                                                                                 initials,
                                                                                 onClick,
                                                                                 disabled,
                                                                                 notificationVariant,
                                                                                 className,
                                                                                 tabIndex,
                                                                                 ...rest
                                                                               }, ref) => {
  const theme = useTheme();
  const renderIcon = () => {
    return (
      (portraitSrc && (
        <ImageWrapper>
          <img src={portraitSrc}/>
          <PortraitOverlay/>
        </ImageWrapper>
      )) ||
      (initials && (
        <ComponentS className="initials" textStyle={ComponentTextStyle.Bold}
                    color={disabled ? COLORS.getColor('neutral_300', theme) : COLORS.getColor('white', theme)}>
          {initials}
        </ComponentS>
      )) || <>{icon}</>
    );
  };

  return (
    <ProfileButtonContainer>
      <IconButton ref={ref}
                  className={className}
                  disabled={disabled}
                  action={onClick}
                  variant="primary"
                  tabIndex={tabIndex}
                  shape="circular"
                  {...rest}>
        {renderIcon()}
        {notificationVariant && (
          <NotificationContainer>
            <NotificationDot size={Size.Medium} variant={notificationVariant}/>
          </NotificationContainer>
        )}
      </IconButton>
    </ProfileButtonContainer>
  );
});

export default ProfileButton;
